import React from "react";
import { useForm, FieldError } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Modal from "../common/Modal";
import Input from "../common/Input";
import Button from "../common/Button";
import {
  screenTagCreateSchema,
  screenTagUpdateSchema,
  ScreenTagFormData,
  ScreenTagUpdateFormData,
  ScreenTag,
} from "../../schemas/screenTagSchema";

interface ScreenTagFormProps {
  isOpen: boolean;
  initialData?: ScreenTagFormData | ScreenTagUpdateFormData | ScreenTag;
  onSubmit: (data: ScreenTagFormData | ScreenTagUpdateFormData) => void;
  onCancel: () => void;
  isLoading?: boolean;
  isUpdate?: boolean;
}

const ScreenTagForm: React.FC<ScreenTagFormProps> = ({
  isOpen,
  initialData,
  onSubmit,
  onCancel,
  isLoading = false,
  isUpdate = false,
}) => {
  const {
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    trigger,
  } = useForm<ScreenTagFormData | ScreenTagUpdateFormData>({
    resolver: zodResolver(
      isUpdate ? screenTagUpdateSchema : screenTagCreateSchema
    ),
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues: initialData
      ? {
          name:
            (initialData as ScreenTag).name ||
            (initialData as ScreenTagFormData).name ||
            "",
          description:
            (initialData as ScreenTag).description ||
            (initialData as ScreenTagFormData).description ||
            "",
        }
      : { name: "", description: "" },
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      title={initialData ? "Edit Screen Tag" : "Create Screen Tag"}
      size="md"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" noValidate>
        <Input
          label="Name"
          placeholder="Enter name"
          name="name"
          value={watch("name") || ""}
          onChange={(e) => setValue("name", e.target.value, { shouldValidate: false })}
          onBlur={() => trigger("name")}
          error={errors.name as FieldError | undefined}
        />

        <Input
          label="Description"
          placeholder="Optional"
          name="description"
          value={watch("description") || ""}
          onChange={(e) => setValue("description", e.target.value, { shouldValidate: false })}
          onBlur={() => trigger("description")}
          error={errors.description as FieldError | undefined}
        />

        <div className="flex justify-end space-x-4">
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button type="submit" variant="primary" isLoading={isLoading}>
            {initialData ? "Update" : "Create"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default ScreenTagForm;
